import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Clock, Copy, Download, Inbox, Mail, MessageCircle, Phone, Plus, RefreshCw, Search, X } from 'lucide-react';
import { TableSkeleton, LinesSkeleton } from '@components/medicus-ui/Loaders';
import { EmailTitularModal } from '@components/EmailTitularModal';
import { AvisoAtribucionModal, AvisoAtribucionTexto } from '@components/AvisoAtribucionOferta';
import {
  CANALES,
  claimContacto,
  DISPOSICION_LABEL,
  DISPOSICIONES,
  DISPOSICIONES_CON_FECHA,
  DISPOSICIONES_CON_MOTIVO,
  getBinaryEnVivo,
  getDisponibles,
  getFichaTmk,
  getMiCartera,
  getMiDia,
  getHistorialContacto,
  liberarContacto,
  MOTIVO_PERDIDA_LABEL,
  MOTIVOS_PERDIDA,
  SKYTEL_CONVERSION_LABEL,
  SKYTEL_PROCESO_LABEL,
  regenerarLinkPago,
  registrarInteraccion,
  resolverLegajo,
  tomarContacto,
  setWhatsapp,
  waLinkConMensaje,
  type AtribucionPago,
  type CarteraFiltros,
  type ContactoTmk,
  type DisposicionTmk,
  type EstadoCrmEfectivo,
  type EventoHistorial,
  type FichaBinaryEnVivo,
  type FichaTmk,
  type MotivoPerdida,
  type PagoFicha,
  type RegistrarInteraccionInput,
  type TmkCrmItem,
} from '@lib/services/tmk-crm.service';
import { copiarLinkOferta, enviarOfertaTmk } from '@lib/services/oferta.service';
import { buscarPersonas, type BusquedaMatchItem } from '@lib/services/busqueda.service';
import { TmkExportModal } from '@components/TmkExportModal';
import { EstadoLegajoBadge } from '@components/EstadoLegajoBadge';
import { useAuthStore } from '@lib/store/auth.store';
import { UserRole } from '@lib/types';

const PAGE_SIZE = 50;

// ─── Formatters / catálogos de presentación ──────────────────────────

function fmtMoney(n: number): string {
  return n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
}

/** Antigüedad legible ("hace 10 min" / "hace 3 días") para reforzar speed-to-lead. */
function hace(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} ${d === 1 ? 'día' : 'días'}`;
}

// El estado de onboarding (legajo) se pinta con <EstadoLegajoBadge> (catálogo
// único en lib/estado-legajo.ts). El enum de CRM/cartera de abajo es propio del
// telemarketer y NO es estado de legajo.

const ESTADO_CRM_LABEL: Record<EstadoCrmEfectivo, string> = {
  sin_asignar: 'Sin asignar',
  en_gestion: 'En gestión',
  contactado: 'Contactado',
  callback_agendado: 'Callback',
  oferta_enviada: 'Oferta enviada',
  pago_iniciado: 'Pago iniciado',
  ganado: 'Ganado',
  perdido: 'Perdido',
  no_contactar: 'No contactar',
};

const ESTADO_CRM_TONE: Record<EstadoCrmEfectivo, string> = {
  sin_asignar: 'status-info',
  en_gestion: 'status-info',
  contactado: 'status-info',
  callback_agendado: 'status-warning',
  oferta_enviada: 'status-warning',
  pago_iniciado: 'status-warning',
  ganado: 'status-success',
  perdido: 'status-error',
  no_contactar: 'status-error',
};

const TRAMO_LABEL: Record<string, string> = {
  'menor-45': 'Menor de 45',
  '45-59': '45 a 59',
  '60-mas': '60 y más',
};

const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
  '13': 'Adherente',
};

function apiError(err: unknown): { code?: string; message: string } {
  const data = (
    err as {
      response?: {
        data?: { code?: string; message?: string; error?: { code?: string; message?: string } };
      };
    }
  ).response?.data;
  return {
    code: data?.error?.code ?? data?.code,
    message: data?.error?.message ?? data?.message ?? 'Algo salió mal. Reintentá.',
  };
}

// ─── Página ───────────────────────────────────────────────────────────

type Tab = 'disponibles' | 'cartera' | 'mi-dia';

/** UUID v4 — para distinguir el legajoId interno del número de legajo en `?legajo=`. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function TmkCrmPage() {
  // La ficha vive en la URL para soportar el deep-link del dialer de SkyTel:
  // ?codeCliente=<webId> / ?dni=<dni> la resuelven y abren. `?legajo=` admite
  // dos formas: el UUID interno (lo setea la propia pantalla al abrir una ficha)
  // → abre directo; o el NÚMERO de legajo (GN008841) → se resuelve como los
  // demás identificadores. Distinguimos por la forma del valor.
  const [searchParams, setSearchParams] = useSearchParams();
  const legajoParam = searchParams.get('legajo');
  const esUuid = !!legajoParam && UUID_RE.test(legajoParam);
  const fichaId = esUuid ? legajoParam : null;
  const legajoNumeroParam = legajoParam && !esUuid ? legajoParam : null;
  const codeClienteParam = searchParams.get('codeCliente');
  const dniParam = searchParams.get('dni');
  // Deep-link de SkyTel: con `tomarContacto=true` el contacto se auto-asigna
  // (pisando al dueño anterior) al agente que abre el link.
  const tomarParam = searchParams.get('tomarContacto') === 'true';

  const [tab, setTab] = useState<Tab>('disponibles');
  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState<CarteraFiltros>({});
  const [exportOpen, setExportOpen] = useState(false);
  const queryClient = useQueryClient();
  // El agente externo de SkyTel usa la pantalla en modo SOLO-búsqueda: la cola
  // interna (Disponibles/Mi cartera/Mi día) y la descarga quedan ocultas (D2).
  const { user } = useAuthStore();
  const esSkytel = user?.rol === UserRole.SKYTEL;

  const q = useQuery({
    queryKey: ['tmk-crm', tab, page, filtros],
    queryFn: () =>
      tab === 'disponibles'
        ? getDisponibles(page, PAGE_SIZE, filtros)
        : getMiCartera(page, PAGE_SIZE, filtros),
    enabled: tab !== 'mi-dia' && !esSkytel,
    placeholderData: (prev) => prev,
  });

  function refrescar() {
    queryClient.invalidateQueries({ queryKey: ['tmk-crm'] });
  }

  function abrirFicha(legajoId: string) {
    const next = new URLSearchParams(searchParams);
    next.set('legajo', legajoId);
    next.delete('codeCliente');
    next.delete('dni');
    next.delete('tomarContacto'); // ya se procesó: no re-tomar en un refresh
    setSearchParams(next);
  }

  function cerrarFicha() {
    const next = new URLSearchParams(searchParams);
    next.delete('legajo');
    next.delete('codeCliente');
    next.delete('dni');
    next.delete('tomarContacto');
    setSearchParams(next);
  }

  function cambiarTab(next: Tab) {
    setTab(next);
    setPage(1);
    setFiltros({});
  }

  function aplicarFiltro(patch: Partial<CarteraFiltros>) {
    setPage(1);
    setFiltros((f) => ({ ...f, ...patch }));
  }

  const total = q.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section>
      <header className="pageHeader rowBetween" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 className="heading-3">CRM Telemarketer</h1>
          <p className="text-element-mid">
            {esSkytel
              ? 'Buscá el titular de la llamada y ofrecé el '
              : 'Buscá un titular para la llamada o gestioná tu cartera, y ofrecé el '}
            <strong>Plan Superador (MS2)</strong>.
          </p>
        </div>
        {!esSkytel && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setExportOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
          >
            <Download size={16} /> Descargar lista
          </button>
        )}
      </header>

      <BuscadorTmk onAbrir={abrirFicha} />

      {!esSkytel && (
        <>
      <div style={{ display: 'flex', gap: 8, marginTop: 16, borderBottom: '1px solid var(--color-outline)' }}>
        <TabButton active={tab === 'disponibles'} onClick={() => cambiarTab('disponibles')}>
          Disponibles para tomar
        </TabButton>
        <TabButton active={tab === 'cartera'} onClick={() => cambiarTab('cartera')}>
          Mi cartera
        </TabButton>
        <TabButton active={tab === 'mi-dia'} onClick={() => cambiarTab('mi-dia')}>
          Mi día
        </TabButton>
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === 'mi-dia' ? (
          <MiDiaView onAbrir={abrirFicha} />
        ) : (
          <>
            {(tab === 'cartera' || tab === 'disponibles') && (
              <FiltrosCartera
                filtros={filtros}
                onChange={aplicarFiltro}
                mostrarEstado={tab === 'cartera'}
              />
            )}
            {q.isLoading && <TableSkeleton rows={6} />}
            {q.isError && (
              <p className="status status-error">No se pudo cargar la lista. Reintentá.</p>
            )}
            {q.data && q.data.items.length === 0 && (
              <EmptyState
                mensaje={
                  tab === 'disponibles'
                    ? 'No hay contactos disponibles en la cola.'
                    : 'Todavía no tomaste ningún contacto.'
                }
              />
            )}
            {q.data && q.data.items.length > 0 && (
              <ContactosTable
                modo={tab === 'cartera' ? 'cartera' : 'disponibles'}
                items={q.data.items}
                total={total}
                onAbrir={abrirFicha}
                onCambio={refrescar}
              />
            )}

            {totalPages > 1 && <Paginador page={page} totalPages={totalPages} onChange={setPage} />}
          </>
        )}
      </div>
        </>
      )}

      {fichaId ? (
        <FichaDrawer legajoId={fichaId} modo={tab} onClose={cerrarFicha} onCambio={refrescar} />
      ) : codeClienteParam || dniParam || legajoNumeroParam ? (
        <ResolverFicha
          codeCliente={codeClienteParam}
          dni={dniParam}
          legajoNumero={legajoNumeroParam}
          tomar={tomarParam}
          onResolved={abrirFicha}
          onClose={cerrarFicha}
        />
      ) : null}

      {exportOpen && <TmkExportModal onClose={() => setExportOpen(false)} />}
    </section>
  );
}

// ─── Buscador (deep-link a una ficha durante la llamada) ────────────────

function BuscadorTmk({ onAbrir }: { onAbrir: (legajoId: string) => void }) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const q = useQuery({
    queryKey: ['tmk-busqueda', submitted],
    queryFn: () => buscarPersonas(submitted),
    enabled: submitted.length >= 2,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(query.trim());
  }
  function limpiar() {
    setQuery('');
    setSubmitted('');
  }

  const matches = q.data?.matches ?? [];

  return (
    <section style={{ ...panelBox, marginTop: 16 }}>
      <h4 className="heading-6" style={{ margin: '0 0 8px' }}>
        Buscar titular para la llamada
      </h4>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8 }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            border: '1px solid var(--color-outline)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-background)',
          }}
        >
          <Search size={16} strokeWidth={2} style={{ opacity: 0.6 }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="DNI, código de cliente (webId), N° de legajo o nombre"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              padding: '10px 0',
              background: 'transparent',
              fontSize: 14,
            }}
          />
          {query && (
            <button
              type="button"
              onClick={limpiar}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              title="Limpiar"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button type="submit" className="btn btn-primary" disabled={query.trim().length < 2}>
          Buscar
        </button>
      </form>

      <div style={{ marginTop: 12 }}>
        {q.isLoading && <LinesSkeleton lines={3} />}
        {q.isError && (
          <p className="status status-error" style={{ display: 'block' }}>
            No pudimos buscar. Reintentá.
          </p>
        )}
        {q.data && matches.length === 0 && submitted && (
          <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
            Sin resultados para “{submitted}”.
          </p>
        )}
        {matches.length > 0 && (
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>DNI</th>
                  <th>Nombre</th>
                  <th>Parentesco</th>
                  <th>Legajo</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {matches.map((m) => (
                  <BusquedaFila key={m.asociadoId} m={m} onAbrir={onAbrir} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function BusquedaFila({ m, onAbrir }: { m: BusquedaMatchItem; onAbrir: (legajoId: string) => void }) {
  const abrible = Boolean(m.legajoId);
  return (
    <tr
      onClick={() => abrible && onAbrir(m.legajoId)}
      style={{ cursor: abrible ? 'pointer' : 'default', opacity: abrible ? 1 : 0.6 }}
    >
      <td style={{ fontFamily: 'monospace' }}>{m.documento}</td>
      <td>
        {m.apellSolt} {m.nombres}
        {m.matchTipo === 'familiar' && m.titularNombre && (
          <span style={{ color: 'var(--color-element-mid)', fontSize: 12 }}>
            {' '}
            · titular {m.titularNombre}
          </span>
        )}
      </td>
      <td>{PARENTESCO_LABEL[m.parentesco] ?? m.parentesco}</td>
      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{m.legajoNumero}</td>
      <td>
        <EstadoLegajoBadge estado={m.legajoEstado} size={11} />
      </td>
      <td style={{ textAlign: 'right', fontSize: 12 }}>
        {abrible ? (
          <span style={{ color: 'var(--color-primary-darker)' }}>Abrir ficha ›</span>
        ) : (
          <span style={{ color: 'var(--color-element-mid)' }}>sin legajo</span>
        )}
      </td>
    </tr>
  );
}

// ─── Resolver de deep-link (codeCliente / legajo nº / dni → ficha) ──────

function ResolverFicha(props: {
  codeCliente: string | null;
  dni: string | null;
  legajoNumero: string | null;
  tomar: boolean;
  onResolved: (legajoId: string) => void;
  onClose: () => void;
}) {
  const { codeCliente, dni, legajoNumero, tomar, onResolved, onClose } = props;
  const yaProceso = useRef(false);
  const q = useQuery({
    queryKey: ['tmk-resolver', codeCliente, dni, legajoNumero],
    queryFn: () =>
      resolverLegajo({
        codeCliente: codeCliente ?? undefined,
        dni: dni ?? undefined,
        legajo: legajoNumero ?? undefined,
      }),
    retry: false,
  });
  // Con `tomarContacto=true` (deep-link de SkyTel): tomamos el contacto (pisando
  // al dueño anterior) y recién ahí abrimos la ficha. Si el take falla (fuera de
  // cohorte / no ofertable), igual abrimos para que el agente vea el legajo.
  const tomarMut = useMutation({ mutationFn: (id: string) => tomarContacto(id) });

  useEffect(() => {
    const id = q.data?.legajoId;
    if (!id || yaProceso.current) return;
    yaProceso.current = true;
    if (tomar) {
      tomarMut.mutate(id, { onSettled: () => onResolved(id) });
    } else {
      onResolved(id);
    }
    // onResolved cambia la URL y desmonta este componente; sólo nos importa el id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.legajoId]);

  const refTexto = codeCliente
    ? `el código ${codeCliente}`
    : legajoNumero
      ? `el legajo ${legajoNumero}`
      : `el DNI ${dni}`;

  return (
    <Overlay onClose={onClose}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(420px, 100%)', display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {q.isError ? (
          <>
            <h3 className="heading-5" style={{ margin: 0 }}>
              No encontramos el legajo
            </h3>
            <p className="text-element-mid" style={{ margin: 0 }}>
              {apiError(q.error).message} (buscando por {refTexto}).
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="heading-5" style={{ margin: 0 }}>
              Abriendo ficha…
            </h3>
            <p className="text-element-mid" style={{ margin: 0, fontSize: 13 }}>
              Buscando {refTexto}.
            </p>
            <LinesSkeleton lines={3} />
          </>
        )}
      </div>
    </Overlay>
  );
}

// ─── Tabla de contactos ───────────────────────────────────────────────

function ContactosTable(props: {
  modo: 'disponibles' | 'cartera';
  items: TmkCrmItem[];
  total: number;
  onAbrir: (legajoId: string) => void;
  onCambio: () => void;
}) {
  const { modo, items, total, onAbrir, onCambio } = props;
  const queryClient = useQueryClient();

  const claim = useMutation({
    mutationFn: (legajoId: string) => claimContacto(legajoId),
    onSuccess: (_d, legajoId) => {
      queryClient.invalidateQueries({ queryKey: ['tmk-crm'] });
      onCambio();
      onAbrir(legajoId); // al tomarlo, abrimos su ficha para empezar a trabajarlo
    },
  });
  const release = useMutation({
    mutationFn: (legajoId: string) => liberarContacto(legajoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmk-crm'] });
      onCambio();
    },
  });

  return (
    <>
      <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 8 }}>
        {total} {total === 1 ? 'contacto' : 'contactos'}
      </p>
      {claim.isError && (
        <p className="status status-error" style={{ display: 'block', marginBottom: 8 }}>
          {apiError(claim.error).message}
        </p>
      )}
      <div className="tableWrap">
        <table className="dataTable">
          <thead>
            <tr>
              <th>Titular</th>
              <th>DNI</th>
              <th>Legajo</th>
              {modo === 'cartera' ? <th>Estado CRM</th> : <th>Estado</th>}
              {modo === 'cartera' && <th>Contacto</th>}
              <th>Elección</th>
              <th>Plan actual</th>
              <th>SkyTel</th>
              <th>Plan MS2 (s/IVA)</th>
              {modo === 'disponibles' ? <th>Pidió contacto</th> : <th>Tomado</th>}
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={it.legajoId}
                onClick={() => onAbrir(it.legajoId)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  {it.titular.apellido} {it.titular.nombres}
                </td>
                <td style={{ fontFamily: 'monospace' }}>{it.titular.documento || '—'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{it.numero}</td>
                {modo === 'cartera' ? (
                  <td>
                    <span className={`status ${ESTADO_CRM_TONE[it.estadoCrm]}`} style={{ fontSize: 11 }}>
                      {ESTADO_CRM_LABEL[it.estadoCrm]}
                    </span>
                  </td>
                ) : (
                  <td>
                    <EstadoLegajoBadge estado={it.estadoOnboarding} size={11} />
                  </td>
                )}
                {modo === 'cartera' && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <ContactoInline contacto={it.titular.contacto} nombre={it.titular.nombres} />
                  </td>
                )}
                <td>
                  <EleccionBadge planElegido={it.planElegido} pidioContacto={it.pidioContacto} />
                </td>
                <td>
                  <PlanActualBadge plan={it.planActual} syncedAt={it.planActualSyncedAt} />
                </td>
                <td>
                  <SkytelBadge skytel={it.skytel} />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtMoney(it.montoMs2SinIva)}</td>
                <td style={{ fontSize: 13 }}>{hace(it.solicitadoAt)}</td>
                <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  {modo === 'disponibles' ? (
                    <button
                      type="button"
                      className="btn btn-primary btn--sm"
                      disabled={claim.isPending}
                      onClick={() => claim.mutate(it.legajoId)}
                    >
                      Tomar
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost btn--sm"
                      disabled={release.isPending}
                      onClick={() => release.mutate(it.legajoId)}
                    >
                      Liberar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Iconos de contacto compactos para la grilla de "Mi cartera". */
function ContactoInline({ contacto, nombre }: { contacto: ContactoTmk; nombre: string }) {
  const wa = contacto.celular.valido && contacto.celular.e164;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {contacto.email && (
        <a href={`mailto:${contacto.email}`} title={contacto.email} style={iconLink}>
          <Mail size={15} />
        </a>
      )}
      {contacto.celular.display && (
        <a href={`tel:+${contacto.celular.e164}`} title={contacto.celular.display} style={iconLink}>
          <Phone size={15} />
        </a>
      )}
      {wa && (
        <a
          href={waLinkConMensaje(contacto.celular.e164!, msgWhatsapp(nombre))}
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir WhatsApp"
          style={{ ...iconLink, color: '#25D366' }}
        >
          <MessageCircle size={15} />
        </a>
      )}
    </div>
  );
}

// ─── Ficha 360 ─────────────────────────────────────────────────────────

function FichaDrawer(props: { legajoId: string; modo: Tab; onClose: () => void; onCambio: () => void }) {
  const { legajoId, modo, onClose, onCambio } = props;
  const { user } = useAuthStore();
  const esSkytel = user?.rol === UserRole.SKYTEL;
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['tmk-crm', 'ficha', legajoId], queryFn: () => getFichaTmk(legajoId) });

  const claim = useMutation({
    mutationFn: () => claimContacto(legajoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmk-crm'] });
      onCambio();
    },
  });
  const release = useMutation({
    mutationFn: () => liberarContacto(legajoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmk-crm'] });
      onCambio();
      onClose();
    },
  });

  return (
    <Overlay onClose={onClose}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(820px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <header className="rowBetween" style={{ alignItems: 'baseline' }}>
          <div>
            <h3 className="heading-5" style={{ margin: 0 }}>
              {q.data ? `${q.data.titular.apellido} ${q.data.titular.nombres}` : 'Cargando…'}
            </h3>
            {q.data && (
              <p
                className="text-element-mid"
                style={{
                  fontSize: 12,
                  marginTop: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                Legajo {q.data.legajo.numero} ·{' '}
                <EstadoLegajoBadge estado={q.data.legajo.estadoOnboarding} size={11} />
                {q.data.ms2Alta?.plan ? (
                  <>
                    {' · plan actual '}
                    <PlanActualBadge
                      plan={q.data.ms2Alta.plan}
                      syncedAt={q.data.ms2Alta.syncedAt}
                    />
                  </>
                ) : null}
                {' · '}
                <PagoBadge pago={q.data.pago} />
              </p>
            )}
          </div>
          <button type="button" className="btn btn-ghost btn--sm" onClick={onClose}>
            Cerrar
          </button>
        </header>

        <div style={{ overflow: 'auto', flex: 1, paddingRight: 8, display: 'grid', gap: 16 }}>
          {q.isLoading && <LinesSkeleton lines={6} />}
          {q.isError && <p className="status status-error">No pudimos cargar la ficha. Reintentá.</p>}
          {q.data && (
            <>
              <div className="rowBetween" style={{ alignItems: 'center', gap: 12 }}>
                <span className={`status ${ESTADO_CRM_TONE[q.data.crm.estado]}`}>
                  {ESTADO_CRM_LABEL[q.data.crm.estado]}
                </span>
                {!esSkytel &&
                  (q.data.crm.asignado ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn--sm"
                      disabled={release.isPending}
                      onClick={() => release.mutate()}
                    >
                      Liberar contacto
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary btn--sm"
                      disabled={claim.isPending}
                      onClick={() => claim.mutate()}
                    >
                      Tomar contacto
                    </button>
                  ))}
              </div>
              {(claim.isError || release.isError) && (
                <p className="status status-error" style={{ display: 'block', margin: 0 }}>
                  {apiError(claim.error ?? release.error).message}
                </p>
              )}

              <EstadoRealPanel legajoId={legajoId} />
              <EleccionFicha eleccion={q.data.eleccion} />
              <ContactoPanel ficha={q.data} />
              {q.data.crm.asignado && !esSkytel && <GestionPanel legajoId={legajoId} />}
              <PrecioPanel ficha={q.data} />
              <GrupoPanel ficha={q.data} />
              <OfertaPanel ficha={q.data} legajoId={legajoId} modo={modo} />
              <TimelineSection legajoId={legajoId} />
            </>
          )}
        </div>
      </div>
    </Overlay>
  );
}

function ContactoPanel({ ficha }: { ficha: FichaTmk }) {
  const c = ficha.titular.contacto;
  const wa = c.celular.valido && c.celular.e164;
  return (
    <section style={panelBox}>
      <h4 className="heading-6" style={{ margin: '0 0 10px' }}>
        Contacto
      </h4>
      <div style={{ display: 'grid', gap: 10 }}>
        <FilaContacto
          icono={<Mail size={16} />}
          valor={c.email ?? 'Sin email'}
          acciones={
            c.email && (
              <>
                <a href={`mailto:${c.email}`} className="btn btn-ghost btn--sm">
                  Enviar mail
                </a>
                <BotonCopiar texto={c.email} />
              </>
            )
          }
        />
        <FilaContacto
          icono={<Phone size={16} />}
          valor={c.celular.display ?? 'Sin celular válido'}
          acciones={
            wa && (
              <>
                <a href={`tel:+${c.celular.e164}`} className="btn btn-ghost btn--sm">
                  Llamar
                </a>
                <BotonCopiar texto={c.celular.display!} />
              </>
            )
          }
        />
        <FilaContacto
          icono={<MessageCircle size={16} color="#25D366" />}
          valor={
            c.tieneWhatsapp === true
              ? 'Tiene WhatsApp'
              : c.tieneWhatsapp === false
                ? 'Sin WhatsApp'
                : 'WhatsApp sin verificar'
          }
          acciones={
            wa && (
              <a
                href={waLinkConMensaje(c.celular.e164!, msgWhatsapp(ficha.titular.nombres))}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn--sm"
              >
                Abrir WhatsApp
              </a>
            )
          }
        />
      </div>
    </section>
  );
}

function PrecioPanel({ ficha }: { ficha: FichaTmk }) {
  const p = ficha.precioMs2;
  return (
    <section
      style={{
        ...panelBox,
        background: 'linear-gradient(135deg, rgba(0,37,100,0.04) 0%, rgba(10,61,143,0.04) 100%)',
        border: '1px solid rgba(0,37,100,0.12)',
      }}
    >
      <h4 className="heading-6" style={{ margin: '0 0 4px', color: '#002564' }}>
        {p.planNombre} — detalle de precio
      </h4>
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
        Vigencia {p.vigencia} · permanencia mínima {p.permanenciaMinimaMeses} meses · mora máx.{' '}
        {p.moraMaxDias} días.
      </p>
      <div className="tableWrap">
        <table className="dataTable">
          <thead>
            <tr>
              <th>Integrante</th>
              <th>Edad</th>
              <th>Tramo</th>
              <th style={{ textAlign: 'right' }}>Cuota/mes (s/IVA)</th>
            </tr>
          </thead>
          <tbody>
            {p.desglose.map((d, i) => (
              <tr key={`${d.nombre}-${i}`}>
                <td>{d.nombre || '—'}</td>
                <td>{d.edad}</td>
                <td>{TRAMO_LABEL[d.tramo] ?? d.tramo}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(d.precioSinIva)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} style={{ fontWeight: 700 }}>
                Total del grupo (s/IVA)
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: '#002564' }}>
                {fmtMoney(p.subtotalSinIva)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function GrupoPanel({ ficha }: { ficha: FichaTmk }) {
  return (
    <section style={panelBox}>
      <h4 className="heading-6" style={{ margin: '0 0 8px' }}>
        Grupo familiar ({ficha.grupo.length})
      </h4>
      <div style={{ display: 'grid', gap: 4, fontSize: 13 }}>
        {ficha.grupo.map((g, i) => (
          <div key={`${g.nombre}-${i}`} className="rowBetween">
            <span>
              {g.nombre || '—'}{' '}
              <span style={{ color: 'var(--color-element-mid)', fontSize: 12 }}>
                ({PARENTESCO_LABEL[g.parentesco] ?? 'Integrante'})
              </span>
            </span>
            <span style={{ color: 'var(--color-element-mid)' }}>{g.edad} años</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * SkyTel es una cuenta de integración y no atribuye ventas: ejecuta la acción
 * directo. Cualquier otro rol ve primero el cartel de atribución.
 */
function conAvisoDeAtribucion(esSkytel: boolean, ejecutar: () => void, pedirAviso: () => void) {
  if (esSkytel) ejecutar();
  else pedirAviso();
}

/**
 * Cartel de atribución antes de copiar el link o generar el de pago. Para el
 * link de pago manda la atribución del pago (si ya hubo uno); para la oferta,
 * quien tiene la vigente. Al continuar dispara la acción correspondiente.
 */
function AvisoAccionOferta({
  aviso,
  ficha,
  yoEmail,
  onCerrar,
  onLink,
  onPago,
}: {
  aviso: null | 'link' | 'pago';
  ficha: FichaTmk;
  yoEmail?: string;
  onCerrar: () => void;
  onLink: () => void;
  onPago: () => void;
}) {
  if (!aviso) return null;
  const atribuidoOferta = ficha.oferta.vigente?.generadoPor ?? null;
  const atribuidoA =
    aviso === 'pago' ? (ficha.pago?.atribucion?.agente ?? atribuidoOferta) : atribuidoOferta;
  return (
    <AvisoAtribucionModal
      accion={aviso}
      atribuidoA={atribuidoA}
      yoEmail={yoEmail}
      onCancelar={onCerrar}
      onContinuar={() => {
        onCerrar();
        if (aviso === 'link') onLink();
        else onPago();
      }}
    />
  );
}

function OfertaPanel({ ficha, legajoId, modo }: { ficha: FichaTmk; legajoId: string; modo: Tab }) {
  const { user } = useAuthStore();
  const esSkytel = user?.rol === UserRole.SKYTEL;
  const queryClient = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  const [enviadoEmail, setEnviadoEmail] = useState<string | null>(null);
  const [linkCopiado, setLinkCopiado] = useState<string | null>(null);
  // Cartel de atribución antes de copiar el link o generar el de pago: la venta
  // pasa a ser de quien toca el link (ver AvisoAtribucionOferta). SkyTel es una
  // cuenta de integración y no atribuye, así que no ve el cartel.
  const [aviso, setAviso] = useState<null | 'link' | 'pago'>(null);
  const mutation = useMutation({
    mutationFn: (email?: string) => enviarOfertaTmk(legajoId, email),
    onError: (err) => {
      const e = apiError(err);
      if (e.code === 'TITULAR_SIN_EMAIL') {
        setErrorEmail(null);
        setPedirEmail('oferta');
      } else if (pedirEmail) {
        setErrorEmail(e.message);
      }
    },
    onSuccess: (data) => {
      setEnviadoEmail(data.emailDestinatario);
      setConfirmar(false);
      setPedirEmail(null);
      setErrorEmail(null);
      queryClient.invalidateQueries({ queryKey: ['tmk-crm', 'ficha', legajoId] });
      queryClient.invalidateQueries({ queryKey: ['tmk-crm', 'historial', legajoId] });
    },
  });
  const linkMut = useMutation({
    mutationFn: () => copiarLinkOferta(legajoId),
    onSuccess: (data) => {
      setLinkCopiado(data.link);
      void navigator.clipboard?.writeText(data.link);
      queryClient.invalidateQueries({ queryKey: ['tmk-crm', 'ficha', legajoId] });
      queryClient.invalidateQueries({ queryKey: ['tmk-crm', 'historial', legajoId] });
    },
  });
  const [pagoLink, setPagoLink] = useState<string | null>(null);
  const [pagoRechazo, setPagoRechazo] = useState<string | null>(null);
  // Si el titular no tiene email, el server corta con 400 TITULAR_SIN_EMAIL:
  // abrimos el modal para cargarlo y reintentamos con el email adjunto.
  const [pedirEmail, setPedirEmail] = useState<null | 'pago' | 'oferta'>(null);
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const regenerarPagoMut = useMutation({
    mutationFn: (email?: string) => regenerarLinkPago(legajoId, email),
    onError: (err) => {
      const e = apiError(err);
      if (e.code === 'TITULAR_SIN_EMAIL') {
        setErrorEmail(null);
        setPedirEmail('pago');
      } else if (pedirEmail) {
        // Error del server con el modal abierto (dominio inválido / email en uso).
        setErrorEmail(e.message);
      }
    },
    onSuccess: (data) => {
      setPagoLink(null);
      setPagoRechazo(null);
      setPedirEmail(null);
      setErrorEmail(null);
      if (data.urlCheckout) {
        setPagoLink(data.urlCheckout);
        void navigator.clipboard?.writeText(data.urlCheckout);
      } else {
        // El gateway aceptó la intención pero no devolvió URL (rechazo directo).
        setPagoRechazo(
          data.motivoRechazo ?? 'El gateway no devolvió un link de pago. Reintentá en unos minutos.',
        );
      }
      queryClient.invalidateQueries({ queryKey: ['tmk-crm', 'ficha', legajoId] });
      queryClient.invalidateQueries({ queryKey: ['tmk-crm', 'historial', legajoId] });
    },
  });
  // El link de pago aplica cuando el titular ya aceptó MS2 y el pago no está
  // aprobado. Si todavía no hay pago, el botón GENERA el primero; si ya hubo, lo
  // REGENERA (el anterior pudo haber caducado en el gateway).
  const puedeRegenerarPago = ficha.eleccion.plan === 'MS2' && ficha.pagoEstado !== 'aprobado';
  const { oferta, titular } = ficha;
  // A quién está atribuida HOY la oferta vigente (si venció, el link nuevo es
  // del que lo genera y no hay nada que avisar).
  const atribuidoOferta = oferta.vigente?.generadoPor ?? null;

  return (
    <section style={panelBox}>
      <div className="rowBetween" style={{ alignItems: 'center', gap: 12 }}>
        <h4 className="heading-6" style={{ margin: 0 }}>
          Oferta MS2
        </h4>
        {!confirmar && (
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              // No bloqueante por "ya eligió MS2": el endpoint /link reenvía el
              // MISMO link (lo reusa si está vigente) y registra cada copia en el
              // historial. Solo lo frena un pago aprobado o un estado no ofertable.
              disabled={!oferta.reenviableLink || linkMut.isPending}
              title={
                oferta.reenviableLink
                  ? oferta.enviada
                    ? 'Reenvía el mismo link (queda registrado en el historial)'
                    : undefined
                  : oferta.motivoNoElegible
              }
              onClick={() =>
                conAvisoDeAtribucion(esSkytel, () => linkMut.mutate(), () => setAviso('link'))
              }
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Copy size={15} />{' '}
              {linkMut.isPending ? 'Generando…' : oferta.enviada ? 'Copiar link de nuevo' : 'Copiar link'}
            </button>
            {!esSkytel && (
              <button
                type="button"
                className="btn btn-primary btn--sm"
                // Igual que "Copiar link": el mail reenvía el MISMO link aunque ya
                // haya elegido MS2. Solo lo frena pago aprobado / estado no ofertable.
                disabled={!oferta.reenviableLink}
                title={oferta.reenviableLink ? undefined : oferta.motivoNoElegible}
                onClick={() => setConfirmar(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Mail size={15} /> {oferta.enviada ? 'Reenviar por mail' : 'Enviar por mail'}
              </button>
            )}
            {puedeRegenerarPago && (
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={regenerarPagoMut.isPending}
                title="Genera un link de pago NUEVO (el anterior pudo haber caducado en el gateway) y lo copia para pasárselo al titular"
                onClick={() =>
                  conAvisoDeAtribucion(
                    esSkytel,
                    () => regenerarPagoMut.mutate(undefined),
                    () => setAviso('pago'),
                  )
                }
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <RefreshCw size={15} />{' '}
                {regenerarPagoMut.isPending
                  ? 'Generando…'
                  : ficha.pagoEstado
                    ? 'Regenerar link de pago'
                    : 'Generar link de pago'}
              </button>
            )}
          </div>
        )}
      </div>

      <AvisoPago pago={ficha.pago} />

      {!oferta.elegible && oferta.motivoNoElegible && (
        <p style={{ display: 'flex', gap: 6, margin: '10px 0 0', fontSize: 12, color: '#9a3412' }}>
          <AlertCircle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>{oferta.motivoNoElegible}</span>
        </p>
      )}

      {oferta.enviada && (
        <p
          style={{
            fontSize: 12,
            color: oferta.enviada.vigente ? '#475569' : '#9a3412',
            margin: '10px 0 0',
            display: 'flex',
            gap: 6,
          }}
        >
          <AlertCircle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            <strong>Oferta ya enviada por {canalOfertaTexto(oferta.enviada)}</strong> el{' '}
            {new Date(oferta.enviada.generadoAt).toLocaleString('es-AR')}
            {oferta.enviada.vigente
              ? `, vence ${new Date(oferta.enviada.expiraAt).toLocaleString('es-AR')}.`
              : ' · link vencido.'}
            {oferta.enviada.aceptadoAt && (
              <span style={{ color: '#15803d', fontWeight: 600 }}> · Aceptada por el titular.</span>
            )}
          </span>
        </p>
      )}

      {confirmar && (
        <div style={{ marginTop: 12, padding: 12, background: 'var(--color-bg-subtle, #f5f7fa)', borderRadius: 8 }}>
          <p className="text-element-mid" style={{ margin: '0 0 10px', fontSize: 13 }}>
            Se enviará el mail con la oferta MS2 a <strong>{titular.contacto.email ?? '(sin email)'}</strong>.
          </p>
          <div style={{ marginBottom: 10 }}>
            <AvisoAtribucionTexto accion="mail" atribuidoA={atribuidoOferta} yoEmail={user?.email} />
          </div>
          {mutation.isError && (
            <p className="status status-error" style={{ display: 'block', margin: '0 0 8px' }}>
              {apiError(mutation.error).message}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost btn--sm" onClick={() => setConfirmar(false)} disabled={mutation.isPending}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary btn--sm"
              onClick={() => mutation.mutate(undefined)}
              disabled={mutation.isPending || !titular.contacto.email}
            >
              {mutation.isPending ? 'Enviando…' : 'Sí, enviar'}
            </button>
          </div>
        </div>
      )}

      {enviadoEmail && (
        <p className="status status-success" style={{ display: 'block', marginTop: 12 }}>
          ✓ Mail enviado a {enviadoEmail}.
        </p>
      )}

      {linkMut.isError && (
        <p className="status status-error" style={{ display: 'block', marginTop: 12 }}>
          {apiError(linkMut.error).message}
        </p>
      )}

      {linkCopiado && (
        <div style={{ marginTop: 12, padding: 12, background: 'var(--color-bg-subtle, #f5f7fa)', borderRadius: 8 }}>
          <p className="status status-success" style={{ display: 'block', margin: '0 0 6px' }}>
            ✓ Link copiado al portapapeles. Pasáselo al titular por el canal de la llamada.
          </p>
          <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{linkCopiado}</code>
        </div>
      )}

      {regenerarPagoMut.isError && (
        <p className="status status-error" style={{ display: 'block', marginTop: 12 }}>
          {apiError(regenerarPagoMut.error).message}
        </p>
      )}

      {pagoRechazo && (
        <p className="status status-warning" style={{ display: 'block', marginTop: 12 }}>
          {pagoRechazo}
        </p>
      )}

      <AvisoAccionOferta
        aviso={aviso}
        ficha={ficha}
        yoEmail={user?.email}
        onCerrar={() => setAviso(null)}
        onLink={() => linkMut.mutate()}
        onPago={() => regenerarPagoMut.mutate(undefined)}
      />

      {pedirEmail && (
        <EmailTitularModal
          titular={{
            nombre: `${ficha.titular.apellido} ${ficha.titular.nombres}`.trim(),
            documento: ficha.titular.documento,
          }}
          accion={pedirEmail}
          errorServidor={errorEmail}
          enviando={regenerarPagoMut.isPending || mutation.isPending}
          onCancelar={() => {
            setPedirEmail(null);
            setErrorEmail(null);
          }}
          onConfirmar={(email) =>
            pedirEmail === 'pago' ? regenerarPagoMut.mutate(email) : mutation.mutate(email)
          }
        />
      )}

      {pagoLink && (
        <div style={{ marginTop: 12, padding: 12, background: 'var(--color-bg-subtle, #f5f7fa)', borderRadius: 8 }}>
          <p className="status status-success" style={{ display: 'block', margin: '0 0 6px' }}>
            ✓ Link de pago NUEVO copiado. Pasáselo al titular para que pague el MS2 (el anterior queda anulado).
          </p>
          <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{pagoLink}</code>
        </div>
      )}

      {ficha.skytel && <OfertaSkytelInfo skytel={ficha.skytel} />}

      {modo === 'disponibles' && !ficha.crm.asignado && (
        <p className="text-element-mid" style={{ fontSize: 12, marginTop: 10 }}>
          Tip: tomá el contacto antes de ofrecer, así queda en tu cartera.
        </p>
      )}
    </section>
  );
}

/** Estado de sincronización con SkyTel/inConcert en la ficha. Extraído de
 *  `OfertaPanel` para no inflar su complejidad cognitiva (Sonar S3776). */
function OfertaSkytelInfo({ skytel }: { skytel: NonNullable<FichaTmk['skytel']> }) {
  const ext = skytel.estadoExterno;
  const estadoExternoTxt = ext
    ? ` · ${ext.proceso ?? ''}${ext.conversion ? ' / ' + ext.conversion : ''}`
    : '';
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid var(--color-outline)',
        fontSize: 12,
      }}
    >
      <div className="rowBetween" style={{ alignItems: 'center' }}>
        <strong style={{ color: 'var(--color-primary-darker)' }}>SkyTel / inConcert</strong>
        {skytel.sincronizado ? (
          <span style={{ color: '#15803d', fontWeight: 700 }}>● Sincronizado</span>
        ) : (
          <span style={{ color: '#9a3412', fontWeight: 700 }}>● Sin sincronizar</span>
        )}
      </div>
      <div style={{ color: 'var(--color-element-mid)', marginTop: 4, display: 'grid', gap: 2 }}>
        <span>
          codeCliente: <code>{skytel.codeCliente}</code>
          {skytel.contactId ? (
            <>
              {' · '}contactId: <code>{skytel.contactId}</code>
            </>
          ) : null}
        </span>
        {skytel.lastSyncAt && (
          <span>
            Último sync: {new Date(skytel.lastSyncAt).toLocaleString('es-AR')}
            {skytel.ultimoStatus ? ` (${skytel.ultimoStatus})` : ''}
          </span>
        )}
        {skytel.ultimoEstadoEnviado && (
          <span>
            Estado enviado: {skytel.ultimoEstadoEnviado.estadoProceso} /{' '}
            {skytel.ultimoEstadoEnviado.estadoConversion}
          </span>
        )}
        {skytel.ultimoError && (
          <span style={{ color: '#9a3412' }}>Último error: {skytel.ultimoError}</span>
        )}
        {skytel.lastInboundAt && (
          <span>
            Última gestión SkyTel: {new Date(skytel.lastInboundAt).toLocaleString('es-AR')}
            {estadoExternoTxt}
          </span>
        )}
        {skytel.cantidadLlamados != null && (
          <span>
            <strong>Llamados:</strong> {skytel.cantidadLlamados}
          </span>
        )}
        {skytel.tipificacion && (
          <span>
            <strong>Tipificación:</strong> {skytel.tipificacion}
          </span>
        )}
        {skytel.observacion && (
          <span>
            <strong>Observación:</strong> {skytel.observacion}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Sub-componentes / estilos ────────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-primary-darker)' : '2px solid transparent',
        color: active ? 'var(--color-primary-darker)' : 'var(--color-element-mid)',
        fontWeight: active ? 700 : 500,
        fontSize: 14,
        padding: '8px 4px',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function FilaContacto({ icono, valor, acciones }: { icono: ReactNode; valor: string; acciones?: ReactNode }) {
  return (
    <div className="rowBetween" style={{ alignItems: 'center', gap: 12 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        {icono}
        {valor}
      </span>
      <span style={{ display: 'inline-flex', gap: 6 }}>{acciones}</span>
    </div>
  );
}

function BotonCopiar({ texto }: { texto: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-ghost btn--sm"
      onClick={() => {
        void navigator.clipboard?.writeText(texto);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 1500);
      }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <Copy size={13} /> {copiado ? 'Copiado' : 'Copiar'}
    </button>
  );
}

function EmptyState({ mensaje }: { mensaje: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--color-element-mid)' }}>
      <Inbox size={32} style={{ opacity: 0.5 }} />
      <p style={{ marginTop: 12 }}>{mensaje}</p>
    </div>
  );
}

function Paginador({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
      <button className="btn btn-ghost" disabled={page <= 1} onClick={() => onChange(Math.max(1, page - 1))}>
        Anterior
      </button>
      <span className="text-element-mid" style={{ fontSize: 13 }}>
        Página {page} de {totalPages}
      </span>
      <button className="btn btn-ghost" disabled={page >= totalPages} onClick={() => onChange(Math.min(totalPages, page + 1))}>
        Siguiente
      </button>
    </div>
  );
}

function Overlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 100,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}

function msgWhatsapp(nombre: string): string {
  const primer = nombre.trim().split(/\s+/)[0] ?? '';
  return `Hola ${primer}, te contacto de Medicus por el Plan Superador que solicitaste. ¿Tenés un minuto?`;
}

const iconLink: React.CSSProperties = {
  display: 'inline-flex',
  color: 'var(--color-primary-darker)',
  textDecoration: 'none',
};

const panelBox: React.CSSProperties = {
  padding: '14px 16px',
  border: '1px solid var(--color-outline)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-background)',
};

const campoInput: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--color-outline)',
  borderRadius: 'var(--radius-md, 6px)',
  fontSize: 14,
  background: 'var(--color-background)',
};

// ─── Mi día ─────────────────────────────────────────────────────────────

function MiDiaView({ onAbrir }: { onAbrir: (id: string) => void }) {
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['tmk-crm', 'mi-dia'], queryFn: getMiDia });
  const refrescar = () => queryClient.invalidateQueries({ queryKey: ['tmk-crm'] });
  if (q.isLoading) return <TableSkeleton rows={6} />;
  if (q.isError || !q.data) return <p className="status status-error">No se pudo cargar tu día.</p>;
  const d = q.data;
  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div
        className="status status-info"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'fit-content' }}
      >
        <Clock size={14} /> Cola disponible para tomar: {d.disponiblesCount}
      </div>
      <MiDiaBucket titulo="Vencidos" tono="status-error" items={d.vencidos} onAbrir={onAbrir} onCambio={refrescar} />
      <MiDiaBucket titulo="Para hoy" tono="status-warning" items={d.hoy} onAbrir={onAbrir} onCambio={refrescar} />
      <MiDiaBucket
        titulo="Sin próxima acción"
        tono="status-info"
        items={d.sinProximaAccion}
        onAbrir={onAbrir}
        onCambio={refrescar}
      />
    </div>
  );
}

function MiDiaBucket(props: {
  titulo: string;
  tono: string;
  items: TmkCrmItem[];
  onAbrir: (id: string) => void;
  onCambio: () => void;
}) {
  const { titulo, tono, items, onAbrir, onCambio } = props;
  return (
    <section>
      <h3 className="heading-6" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        {titulo} <span className={`status ${tono}`} style={{ fontSize: 11 }}>{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
          Nada acá. 🎉
        </p>
      ) : (
        <ContactosTable modo="cartera" items={items} total={items.length} onAbrir={onAbrir} onCambio={onCambio} />
      )}
    </section>
  );
}

// ─── Gestión: WhatsApp + registrar interacción ──────────────────────────

function GestionPanel({ legajoId }: { legajoId: string }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const wa = useMutation({
    mutationFn: (tiene: boolean | null) => setWhatsapp(legajoId, tiene),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tmk-crm', 'ficha', legajoId] }),
  });
  return (
    <section style={panelBox}>
      <div className="rowBetween" style={{ alignItems: 'center', gap: 12 }}>
        <h4 className="heading-6" style={{ margin: 0 }}>
          Gestión
        </h4>
        <button
          type="button"
          className="btn btn-primary btn--sm"
          onClick={() => setModalOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={15} /> Registrar interacción
        </button>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--color-element-mid)' }}>WhatsApp:</span>
        <button type="button" className="btn btn-ghost btn--sm" disabled={wa.isPending} onClick={() => wa.mutate(true)}>
          Tiene
        </button>
        <button type="button" className="btn btn-ghost btn--sm" disabled={wa.isPending} onClick={() => wa.mutate(false)}>
          No tiene
        </button>
        <button type="button" className="btn btn-ghost btn--sm" disabled={wa.isPending} onClick={() => wa.mutate(null)}>
          Sin verificar
        </button>
      </div>
      {modalOpen && (
        <InteraccionModal
          legajoId={legajoId}
          onClose={() => setModalOpen(false)}
          onDone={() => {
            setModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['tmk-crm'] });
          }}
        />
      )}
    </section>
  );
}

function InteraccionModal(props: { legajoId: string; onClose: () => void; onDone: () => void }) {
  const { legajoId, onClose, onDone } = props;
  const [canal, setCanal] = useState<RegistrarInteraccionInput['canal']>('llamada');
  const [disposicion, setDisposicion] = useState<DisposicionTmk>('no_contesta');
  const [nextActionAt, setNextActionAt] = useState('');
  const [motivoPerdida, setMotivoPerdida] = useState<MotivoPerdida>('precio_alto');
  const [nota, setNota] = useState('');
  const requiereFecha = DISPOSICIONES_CON_FECHA.includes(disposicion);
  const requiereMotivo = DISPOSICIONES_CON_MOTIVO.includes(disposicion);
  const mutation = useMutation({
    mutationFn: () => {
      const input: RegistrarInteraccionInput = {
        canal,
        disposicion,
        nota: nota.trim() || undefined,
        nextActionAt: requiereFecha && nextActionAt ? new Date(nextActionAt).toISOString() : undefined,
        nextActionTipo: requiereFecha ? 'llamar' : undefined,
        motivoPerdida: requiereMotivo ? motivoPerdida : undefined,
      };
      return registrarInteraccion(legajoId, input);
    },
    onSuccess: onDone,
  });
  const faltaFecha = requiereFecha && !nextActionAt;
  return (
    <Overlay onClose={onClose}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(460px, 100%)', display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <h3 className="heading-5" style={{ margin: 0 }}>
          Registrar interacción
        </h3>
        <Campo label="Canal">
          <select
            style={campoInput}
            value={canal}
            onChange={(e) => setCanal(e.target.value as RegistrarInteraccionInput['canal'])}
          >
            {CANALES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Campo>
        <Campo label="Resultado (disposición)">
          <select
            style={campoInput}
            value={disposicion}
            onChange={(e) => setDisposicion(e.target.value as DisposicionTmk)}
          >
            {DISPOSICIONES.map((dd) => (
              <option key={dd} value={dd}>
                {DISPOSICION_LABEL[dd]}
              </option>
            ))}
          </select>
        </Campo>
        {requiereFecha && (
          <Campo label="Próxima acción (fecha y hora)">
            <input
              type="datetime-local"
              style={campoInput}
              value={nextActionAt}
              onChange={(e) => setNextActionAt(e.target.value)}
            />
          </Campo>
        )}
        {requiereMotivo && (
          <Campo label="Motivo de pérdida">
            <select
              style={campoInput}
              value={motivoPerdida}
              onChange={(e) => setMotivoPerdida(e.target.value as MotivoPerdida)}
            >
              {MOTIVOS_PERDIDA.map((m) => (
                <option key={m} value={m}>
                  {MOTIVO_PERDIDA_LABEL[m]}
                </option>
              ))}
            </select>
          </Campo>
        )}
        <Campo label="Nota (solo comercial — no escribir datos de salud)">
          <textarea
            style={{ ...campoInput, resize: 'vertical' }}
            rows={3}
            value={nota}
            maxLength={1000}
            onChange={(e) => setNota(e.target.value)}
          />
        </Campo>
        {mutation.isError && (
          <p className="status status-error" style={{ display: 'block', margin: 0 }}>
            {apiError(mutation.error).message}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => mutation.mutate(undefined)}
            disabled={mutation.isPending || faltaFecha}
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function Campo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>{label}</span>
      {children}
    </label>
  );
}

// ─── Timeline de interacciones ──────────────────────────────────────────

function TimelineSection({ legajoId }: { legajoId: string }) {
  // Histórico consolidado del contacto: asignaciones (tomó/liberó/reasignó),
  // interacciones, entregas de oferta (mail/copia) y pagos atribuidos a la
  // oferta. Más reciente primero (el backend lo devuelve ascendente).
  const q = useQuery({
    queryKey: ['tmk-crm', 'historial', legajoId],
    queryFn: () => getHistorialContacto(legajoId),
  });
  const eventos = q.data ? [...q.data].reverse() : [];
  return (
    <section style={panelBox}>
      <h4 className="heading-6" style={{ margin: '0 0 8px' }}>
        Historial del contacto
      </h4>
      {q.isLoading && <LinesSkeleton lines={3} />}
      {q.data && eventos.length === 0 && (
        <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
          Sin actividad todavía.
        </p>
      )}
      {eventos.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {eventos.map((ev, i) => (
            <TimelineItem key={i} ev={ev} />
          ))}
        </div>
      )}
    </section>
  );
}

const COLOR_EVENTO: Record<EventoHistorial['tipo'], string> = {
  asignacion: '#0a3d8f',
  interaccion: 'var(--color-outline)',
  forzado: '#d97706',
  skytel: '#7c3aed',
  oferta: '#d97706',
  pago: '#16a34a',
};

const ACCION_ASIGNACION_LABEL: Record<string, string> = {
  claim: 'Tomó el contacto',
  release: 'Liberó el contacto',
  reasignacion: 'Reasignado',
  'auto-release': 'Liberado por SLA',
};

/** Texto del canal por el que salió la oferta (chatbot / SkyTel / asesor). */
function canalOfertaTexto(e: { canal: string; agente?: { nombre?: string; email?: string } }): string {
  if (e.canal === 'chatbot') return 'el chatbot (WhatsApp)';
  if (e.canal === 'IB') return 'el chatbot (inbound)';
  if (e.canal === 'OB') return 'el chatbot (outbound)';
  if (e.canal === 'skytel') return 'SkyTel';
  const quien = e.agente?.nombre ?? e.agente?.email;
  if (e.canal === 'medicus') return quien ? `Medicus (${quien})` : 'un operador de Medicus';
  return quien ?? 'un asesor';
}

/** Título de un evento de tipo 'oferta' (envío por mail/copia, o el sintético
 *  del backend para ofertas de chatbot/SkyTel). Extraído de `tituloEvento` para
 *  no inflar su complejidad cognitiva (Sonar S3776). */
function tituloOferta(ev: EventoHistorial): string {
  if (ev.accion === 'oferta-generada') {
    const CANAL_TXT: Record<string, string> = {
      chatbot: 'chatbot (WhatsApp)',
      IB: 'chatbot (inbound)',
      OB: 'chatbot (outbound)',
      skytel: 'SkyTel',
    };
    const canalTxt = CANAL_TXT[ev.canal ?? ''] ?? 'un asesor';
    return `🤖 Oferta enviada por ${canalTxt}`;
  }
  if (ev.accion === 'oferta-aceptada') return '✅ Oferta aceptada por el titular';
  return ev.disposicion === 'link_copiado'
    ? '🔗 Link de oferta copiado'
    : '✉️ Oferta enviada por mail';
}

function tituloEvento(ev: EventoHistorial): string {
  const estado = ev.estado ? ESTADO_CRM_LABEL[ev.estado as EstadoCrmEfectivo] ?? ev.estado : '';
  switch (ev.tipo) {
    case 'asignacion':
      return ACCION_ASIGNACION_LABEL[ev.accion ?? ''] ?? ev.accion ?? 'Asignación';
    case 'forzado':
      return `Estado forzado${estado ? ` → ${estado}` : ''}`;
    case 'skytel':
      return `Actualización de SkyTel${estado ? ` → ${estado}` : ''}`;
    case 'oferta':
      return tituloOferta(ev);
    case 'pago':
      return ev.disposicion === 'pago_aprobado'
        ? '💳 Pago aprobado (vino de la oferta)'
        : '💳 Pago iniciado desde la oferta';
    default: {
      const disp = DISPOSICION_LABEL[ev.disposicion as DisposicionTmk] ?? ev.disposicion ?? 'Interacción';
      return `${disp}${ev.canal ? ` · ${ev.canal}` : ''}`;
    }
  }
}

function TimelineItem({ ev }: { ev: EventoHistorial }) {
  return (
    <div style={{ borderLeft: `2px solid ${COLOR_EVENTO[ev.tipo]}`, paddingLeft: 10 }}>
      <div style={{ fontSize: 13 }}>
        <strong>{tituloEvento(ev)}</strong>
      </div>
      {ev.nota && (
        <div style={{ fontSize: 12, color: 'var(--color-element-mid)', marginTop: 2 }}>“{ev.nota}”</div>
      )}
      <div style={{ fontSize: 11, color: 'var(--color-element-mid)', marginTop: 2 }}>
        {ev.at ? new Date(ev.at).toLocaleString('es-AR') : ''}
      </div>
    </div>
  );
}

// ─── Estado real (qué TIENE hoy: Binary en vivo + pagos + síntesis) ─────

const PAGO_MS2_LABEL: Record<string, string> = {
  aprobado: 'Aprobado',
  pendiente: 'Iniciado, sin completar',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
};

function tonoColor(tono: FichaBinaryEnVivo['sintesis']['tono']): string {
  if (tono === 'success') return '#16a34a';
  if (tono === 'warning') return '#d97706';
  if (tono === 'info') return '#2563eb';
  return '#64748b';
}

function planRealLabel(plan: 'MS1' | 'MS2' | 'otro' | null): string {
  if (plan === 'MS2') return 'Plan Superador (MS2)';
  if (plan === 'MS1') return 'Plan base (MS1)';
  if (plan === 'otro') return 'Otro plan';
  return '—';
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY'; ISO completo → fecha local. */
function fmtFechaCorta(iso?: string | null): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.slice(0, 10));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-AR');
}

/** Texto del horario del dato de alta: en vivo / último sync / sin dato. */
function textoHorarioDato(d: FichaBinaryEnVivo): string {
  if (d.enVivo) {
    const cuando = d.fechaDato ? new Date(d.fechaDato).toLocaleString('es-AR') : 'ahora';
    return `Consultado en vivo · ${cuando}`;
  }
  if (d.fechaDato) {
    return `No se pudo consultar en vivo — último dato sincronizado: ${new Date(
      d.fechaDato,
    ).toLocaleString('es-AR')}`;
  }
  return 'No se pudo consultar en vivo y todavía no hay dato sincronizado.';
}

/** Estado actual del pago MS2 con monto y fecha. */
function textoEstadoPago(p: FichaBinaryEnVivo['pagoMs2']): string {
  if (!p.estado) return '';
  const label = PAGO_MS2_LABEL[p.estado] ?? p.estado;
  const monto = p.monto ? ` · ${fmtMoney(p.monto)}` : '';
  const fecha = p.actualizadoAt ? ` · ${new Date(p.actualizadoAt).toLocaleDateString('es-AR')}` : '';
  return `${label}${monto}${fecha}`;
}

/** Lista de intentos de pago: "Rechazado (05/06) · Iniciado (04/06)". */
function textoIntentos(intentos: FichaBinaryEnVivo['pagoMs2']['intentos']): string {
  return intentos
    .slice()
    .reverse()
    .map((i) => {
      const label = PAGO_MS2_LABEL[i.estado] ?? i.estado;
      return i.at ? `${label} (${fmtFechaCorta(i.at)})` : label;
    })
    .join(' · ');
}

function FilaInfo({ label, valor }: { label: string; valor: ReactNode }) {
  return (
    <div className="rowBetween" style={{ fontSize: 13, marginTop: 4, gap: 12 }}>
      <span>{label}</span>
      <strong style={{ textAlign: 'right' }}>{valor}</strong>
    </div>
  );
}

/**
 * "Qué tiene HOY": síntesis accionable + plan real (Binary en vivo) + pagos MS2.
 * Se consulta automáticamente al abrir la ficha; si PMED no responde, cae al
 * último sync con su horario (lo resuelve el backend, acá solo se muestra).
 */
function EstadoRealPanel({ legajoId }: { legajoId: string }) {
  const q = useQuery({
    queryKey: ['tmk-crm', 'binary', legajoId],
    queryFn: () => getBinaryEnVivo(legajoId),
    staleTime: 60_000,
  });

  if (q.isLoading) {
    return (
      <section style={panelBox}>
        <LinesSkeleton lines={3} />
      </section>
    );
  }
  if (q.isError || !q.data) {
    return (
      <section style={panelBox}>
        <p className="status status-error" style={{ display: 'block', margin: 0 }}>
          No pudimos consultar el estado real en Binary. Reintentá.
        </p>
      </section>
    );
  }

  const d = q.data;
  const color = tonoColor(d.sintesis.tono);
  return (
    <section style={{ ...panelBox, padding: 0, overflow: 'hidden' }}>
      {/* Síntesis accionable */}
      <div style={{ padding: '12px 16px', borderLeft: `4px solid ${color}` }}>
        <strong style={{ fontSize: 14, color }}>{d.sintesis.titulo}</strong>
        <p style={{ margin: '4px 0 0', fontSize: 13 }}>{d.sintesis.detalle}</p>
        {d.sintesis.accion && (
          <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: 600 }}>→ {d.sintesis.accion}</p>
        )}
      </div>

      {/* Plan que tiene hoy (Binary) */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--color-outline)' }}>
        <h4 className="heading-6" style={{ margin: '0 0 6px' }}>
          Plan que tiene hoy{' '}
          <span style={{ fontWeight: 400, color: 'var(--color-element-mid)' }}>· en Binary</span>
        </h4>
        <FilaInfo
          label="¿Dado de alta?"
          valor={
            d.alta.dadoDeAlta
              ? `Sí${d.alta.fechaAlta ? ` · ${fmtFechaCorta(d.alta.fechaAlta)}` : ''}`
              : 'No'
          }
        />
        <FilaInfo label="Plan real" valor={planRealLabel(d.alta.plan)} />
        <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--color-element-mid)' }}>
          {textoHorarioDato(d)}
        </p>
      </div>

      {/* Pagos del MS2 */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--color-outline)' }}>
        <h4 className="heading-6" style={{ margin: '0 0 6px' }}>
          Pagos del MS2
        </h4>
        {d.pagoMs2.estado ? (
          <>
            <FilaInfo label="Estado del pago" valor={textoEstadoPago(d.pagoMs2)} />
            <FilaInfo label="Atribuido a" valor={textoAtribucion(d.pagoMs2.atribucion)} />
            {d.pagoMs2.intentos.length > 1 && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-element-mid)' }}>
                Intentos: {textoIntentos(d.pagoMs2.intentos)}
              </p>
            )}
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-element-mid)' }}>
            No inició ningún pago del MS2.
          </p>
        )}
      </div>
    </section>
  );
}

// ─── Elección del titular (plan elegido / pidió contacto) ───────────────

function planLabel(plan: 'MS2' | 'MS1' | null): string {
  if (plan === 'MS2') return 'Plan Superador (MS2)';
  if (plan === 'MS1') return 'Plan base (MS1)';
  return 'Sin decidir';
}

function EleccionBadge({
  planElegido,
  pidioContacto,
}: {
  planElegido: 'MS2' | 'MS1' | null;
  pidioContacto: boolean;
}) {
  // Prioridad: compró MS2 > pidió que lo contacten > se quedó en MS1 > sin decidir.
  if (planElegido === 'MS2') {
    return <span className="status status-success" style={{ fontSize: 11 }}>Eligió MS2</span>;
  }
  if (pidioContacto) {
    return <span className="status status-warning" style={{ fontSize: 11 }}>Pidió contacto</span>;
  }
  if (planElegido === 'MS1') {
    return <span className="status status-info" style={{ fontSize: 11 }}>MS1 (base)</span>;
  }
  return <span style={{ color: 'var(--color-element-mid)' }}>—</span>;
}

/** Plan que TIENE hoy en Binary (espejo ms2Alta). Distinto de la elección. */
function PlanActualBadge({
  plan,
  syncedAt,
}: {
  plan: 'MS1' | 'MS2' | 'otro' | null;
  syncedAt?: string;
}) {
  const title = syncedAt
    ? `Plan en Binary · últ. sync ${fmtFechaCorta(syncedAt)}`
    : 'Sin sincronizar con Binary';
  if (plan === 'MS2') {
    return <span className="status status-success" style={{ fontSize: 11 }} title={title}>MS2</span>;
  }
  if (plan === 'MS1') {
    return <span className="status status-info" style={{ fontSize: 11 }} title={title}>MS1</span>;
  }
  if (plan === 'otro') {
    return <span className="status status-warning" style={{ fontSize: 11 }} title={title}>Otro</span>;
  }
  return (
    <span style={{ color: 'var(--color-element-mid)' }} title="Sin sincronizar con Binary">
      —
    </span>
  );
}

/**
 * Aviso de pago dentro de la sección Oferta. Va acá y no dentro de `OfertaPanel`
 * para no pasarnos de la complejidad cognitiva que permite Sonar (S3776).
 *
 * Con el pago aprobado el server bloquea generar otro link y el botón no se
 * muestra: sin este cartel el agente cree que la ficha está rota (pasó con
 * GN032835).
 */
function AvisoPago({ pago }: { pago: PagoFicha | null }) {
  if (pago?.estado === 'aprobado') {
    const monto = pago.monto ? ` · ${fmtMoney(pago.monto)}` : '';
    const fecha = pago.fecha ? ` · ${new Date(pago.fecha).toLocaleDateString('es-AR')}` : '';
    return (
      <p style={{ display: 'flex', gap: 6, margin: '10px 0 0', fontSize: 13, color: '#15803d', fontWeight: 600 }}>
        <CheckCircle2 size={15} style={{ marginTop: 1, flexShrink: 0 }} />
        <span>
          Ya pagó el MS2{monto}{fecha}{' · '}
          <span style={{ fontWeight: 400 }}>atribuido a {textoAtribucion(pago.atribucion)}</span>
          <span style={{ display: 'block', fontWeight: 400, color: '#475569', marginTop: 2 }}>
            Por eso no se puede generar un link de pago nuevo. Si el plan todavía figura MS1, el
            cambio está pendiente en Binary — no hace falta volver a ofertar.
          </span>
        </span>
      </p>
    );
  }
  if (pago?.estado === 'pendiente') {
    const monto = pago.monto ? ` (${fmtMoney(pago.monto)})` : '';
    const desde = pago.iniciadoAt
      ? ` desde el ${new Date(pago.iniciadoAt).toLocaleDateString('es-AR')}`
      : '';
    return (
      <p style={{ display: 'flex', gap: 6, margin: '10px 0 0', fontSize: 12, color: '#92400e' }}>
        <AlertCircle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
        <span>
          Tiene un pago iniciado sin confirmar{monto}
          {desde}. Si el link caducó, regeneralo con el botón.
        </span>
      </p>
    );
  }
  return null;
}

/** Texto corto de a quién se le atribuye el pago ("TMK SkyTel · Margarita S."). */
function textoAtribucion(a: AtribucionPago | null | undefined): string {
  if (!a) return 'Sin atribución';
  const quien = a.agente?.nombre;
  return quien ? `${a.canalLabel} · ${quien}` : a.canalLabel;
}

/**
 * Estado del pago MS2 en el encabezado de la ficha. Va arriba de todo y sin
 * scroll a propósito: si el pago está aprobado el server bloquea generar un
 * link nuevo, y sin este dato el agente no entiende por qué.
 */
function PagoBadge({ pago }: { pago: PagoFicha | null }) {
  if (!pago) {
    return (
      <span className="status" style={{ fontSize: 11 }} title="Nunca inició un pago del MS2">
        sin pago
      </span>
    );
  }
  const monto = pago.monto ? ` ${fmtMoney(pago.monto)}` : '';
  const fecha = pago.fecha ? ` · ${fmtFechaCorta(pago.fecha)}` : '';
  const title = `${textoAtribucion(pago.atribucion)}${fecha ? ` · ${fecha.replace(' · ', '')}` : ''}`;
  if (pago.estado === 'aprobado') {
    return (
      <span className="status status-success" style={{ fontSize: 11 }} title={title}>
        PAGÓ{monto}
        {fecha}
      </span>
    );
  }
  if (pago.estado === 'rechazado') {
    return (
      <span className="status status-error" style={{ fontSize: 11 }} title={title}>
        pago rechazado{fecha}
      </span>
    );
  }
  if (pago.estado === 'pendiente') {
    return (
      <span className="status status-warning" style={{ fontSize: 11 }} title={title}>
        pago pendiente{monto}
      </span>
    );
  }
  return (
    <span className="status" style={{ fontSize: 11 }} title={title}>
      pago cancelado
    </span>
  );
}

/** Tono del badge según el resultado comercial que reportó SkyTel. */
function skytelTone(conversion: string | null): string {
  if (conversion === 'ganado') return 'status-success';
  if (conversion === 'en_oferta') return 'status-info';
  if (conversion && conversion.startsWith('perdido')) return 'status-error';
  if (conversion === 'no_contactar') return 'status-error';
  return 'status-warning'; // sin_resultado o solo etapa de proceso
}

/** Estado que reportó SkyTel (inbound) para la fila del CRM. */
function SkytelBadge({ skytel }: { skytel: TmkCrmItem['skytel'] }) {
  if (!skytel || (!skytel.conversion && !skytel.proceso)) {
    return (
      <span style={{ color: 'var(--color-element-mid)' }} title="SkyTel todavía no gestionó este contacto">
        —
      </span>
    );
  }
  const label = skytel.conversion
    ? (SKYTEL_CONVERSION_LABEL[skytel.conversion] ?? skytel.conversion)
    : (SKYTEL_PROCESO_LABEL[skytel.proceso ?? ''] ?? skytel.proceso ?? '—');
  const title = [
    skytel.proceso ? `Etapa: ${SKYTEL_PROCESO_LABEL[skytel.proceso] ?? skytel.proceso}` : null,
    skytel.tipificacion ? `Tipificación: ${skytel.tipificacion}` : null,
    skytel.observacion ? `Obs.: ${skytel.observacion}` : null,
    skytel.cantidadLlamados != null ? `Llamados: ${skytel.cantidadLlamados}` : null,
    skytel.lastInboundAt ? `Reportado: ${fmtFechaCorta(skytel.lastInboundAt)}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  return (
    <span className={`status ${skytelTone(skytel.conversion)}`} style={{ fontSize: 11 }} title={title}>
      {label}
    </span>
  );
}

function EleccionFicha({ eleccion }: { eleccion: FichaTmk['eleccion'] }) {
  return (
    <section style={panelBox}>
      <h4 className="heading-6" style={{ margin: '0 0 2px' }}>
        Qué eligió en el onboarding
      </h4>
      <p style={{ margin: '0 0 8px', fontSize: 11, color: 'var(--color-element-mid)' }}>
        Es lo que pidió al terminar el formulario, no necesariamente lo que tiene hoy.
      </p>
      <div className="rowBetween" style={{ fontSize: 13 }}>
        <span>Plan elegido</span>
        <strong>
          {planLabel(eleccion.plan)}
          {eleccion.decididoAt ? ` · ${new Date(eleccion.decididoAt).toLocaleDateString('es-AR')}` : ''}
        </strong>
      </div>
      <div className="rowBetween" style={{ fontSize: 13, marginTop: 6 }}>
        <span>¿Pidió que lo contacten?</span>
        <strong>
          {eleccion.pidioContacto ? 'Sí' : 'No'}
          {eleccion.pidioContacto && eleccion.solicitadoContactoAt
            ? ` · ${new Date(eleccion.solicitadoContactoAt).toLocaleDateString('es-AR')}`
            : ''}
        </strong>
      </div>
    </section>
  );
}

// ─── Filtros de Mi cartera ──────────────────────────────────────────────

const ESTADOS_CARTERA: EstadoCrmEfectivo[] = [
  'en_gestion',
  'contactado',
  'callback_agendado',
  'oferta_enviada',
  'pago_iniciado',
  'ganado',
  'perdido',
  'no_contactar',
];

function FiltrosCartera({
  filtros,
  onChange,
  mostrarEstado = true,
}: {
  filtros: CarteraFiltros;
  onChange: (patch: Partial<CarteraFiltros>) => void;
  /** El estado CRM sólo aplica a "Mi cartera" (no existe antes de tomar el contacto). */
  mostrarEstado?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
      {mostrarEstado && (
        <FiltroSelect
          label="Estado CRM"
          value={filtros.estado ?? ''}
          onChange={(v) => onChange({ estado: v || undefined })}
          options={[['', 'Todos'], ...ESTADOS_CARTERA.map((e) => [e, ESTADO_CRM_LABEL[e]] as [string, string])]}
        />
      )}
      <FiltroSelect
        label="Elección del titular"
        value={filtros.eleccion ?? ''}
        onChange={(v) => onChange({ eleccion: v || undefined })}
        options={[
          ['', 'Todas'],
          ['MS2', 'Eligió MS2'],
          ['contacto', 'Pidió contacto'],
          ['MS1', 'MS1 (base)'],
          ['sin_decidir', 'Sin decidir'],
        ]}
      />
      <FiltroSelect
        label="Estado SkyTel"
        value={filtros.skytelEstado ?? ''}
        onChange={(v) => onChange({ skytelEstado: v || undefined })}
        options={[
          ['', 'Todos'],
          ['con_feedback', 'Con feedback'],
          ['sin_feedback', 'Sin feedback'],
          ...(Object.entries(SKYTEL_CONVERSION_LABEL) as Array<[string, string]>),
        ]}
      />
    </div>
  );
}

function FiltroSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--color-element-mid)' }}>{label}</span>
      <select
        style={{ ...campoInput, minWidth: 160, padding: '6px 8px' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
